/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import {
  BuilderFormSchemaFactory,
  FormRootSchema,
  FormState,
  FormStateSchema,
  whenCreateOrEditMode,
} from '@gh/builder-shared';
import { ContactType } from '@gh/builder-shared-data';
import { collectVisibleValues, FormAndName, toAppFormGroup } from '@gh/common-shared';
import { FormMode } from '@gh/core-ui';
import { keyBy, mapValues } from 'lodash';
import { ContactDetails, getContactTabOriginalValue } from '../../resources/model/contact-details.model';
import { ContactDetailsModel } from '../../services/contact-details-model.service';
import { ContactDetailsCityCountyFormState } from '../contact-details-city-county-tab/contact-details-city-county-form.service';
import { ContactDetailsCommonFormState } from '../contact-details-common-tab/contact-details-common-form.service';
import { ContactDetailsCreditorFormState } from '../contact-details-creditor-tab/contact-details-creditor-form.service';
import { ContactDetailsCustomerFormState } from '../contact-details-customer-tab/contact-details-customer-form.service';
import { ContactDetailsDebtorFormState } from '../contact-details-debtor-tab/contact-details-debtor-form.service';
import { ContactDetailsEmployeeFormDelegator } from '../contact-details-employee-tab/contact-details-employee-form.service';
import { ContactDetailsFinanceCompanyFormState } from '../contact-details-finance-company-tab/contact-details-finance-company-form.service';
import { ContactDetailsOtherFormState } from '../contact-details-other-tab/contact-details-other-form.service';

@Injectable()
export class ContactDetailsFormState extends FormState<ContactDetails> {

  constructor(private model: ContactDetailsModel,
              schemaFactory: BuilderFormSchemaFactory,
              private commonForm: ContactDetailsCommonFormState,
              private employeeForm: ContactDetailsEmployeeFormDelegator,
              private creditorForm: ContactDetailsCreditorFormState,
              private customerForm: ContactDetailsCustomerFormState,
              private financeCompanyForm: ContactDetailsFinanceCompanyFormState,
              private debtorForm: ContactDetailsDebtorFormState,
              private cityCountyForm: ContactDetailsCityCountyFormState,
              private otherForm: ContactDetailsOtherFormState) {
    super(schemaFactory);
  }

  get submitValue(): ContactDetails {
    const contactDetails: ContactDetails = collectVisibleValues(toAppFormGroup(this.group));
    // traverse all editable contact types and mark them as dirty if they have been changed
    contactDetails.common.contactTypes
      .filter((type) => type.assigned && type.allowed)
      .forEach((type) => {
        type.isDirty = type.isDirty
          // mark type as dirty if contact type options were changed (for example, active/inactive)
          || this.commonForm.getContactTypeFormGroup(type.type).dirty
          // mark type as dirty if contact type-related data were changed
          || this.findTabForm(type.type).form.dirty;
      });
    contactDetails.common.confirmations = {
      ...contactDetails.common.confirmations,
      skipOfficeSetupFlowCheck: this.model.skipOfficeSetupFlowCheck,
    };
    return contactDetails;
  }

  build(details: ContactDetails): void {
    const { model } = this;

    const mode = model.creationMode ?
      FormMode.CreateMode
      : (model.viewMode ? FormMode.ViewMode : FormMode.EditMode);
    this.setMode(mode);

    const tabForms = this.getTabForms(details);

    this.commonForm.build(details);
    tabForms.forEach((form) => form.form.build(details[form.name]));

    this.initSchema(details);

    this.setChildForm('common', this.commonForm);
    tabForms.forEach((form) => this.setChildForm(form.name, form.form));
  }

  findTabForm(type: ContactType): FormAndName {
    return {
      [ContactType.Employee]: {
        name: 'employee',
        form: this.employeeForm,
      },
      [ContactType.CreditorSupplier]: {
        name: 'creditorSupplier',
        form: this.creditorForm,
      },
      [ContactType.Customer]: {
        name: 'customer',
        form: this.customerForm,
      },
      [ContactType.FinanceCompany]: {
        name: 'financeCompany',
        form: this.financeCompanyForm,
      },
      [ContactType.Debtor]: {
        name: 'debtor',
        form: this.debtorForm,
      },
      [ContactType.CityCounty]: {
        name: 'cityCounty',
        form: this.cityCountyForm,
      },
      [ContactType.Other]: {
        name: 'other',
        form: this.otherForm,
      },
    }[type];
  }

  assignTabForm(formAndName: FormAndName, original: ContactDetails): void {
    const { form, name } = formAndName;

    // init new tab merging original data provided by the backend and current form state
    form.build(getContactTabOriginalValue(this.group.value, original, <any>name));

    // we have to mark form as dirty, because only dirty contact types are saved
    form.group.markAsDirty();

    this.setChildForm(name, form);
    this.group.setControl(name, form.group);
  }

  unAssignTabForm(formAndName: FormAndName): void {
    const { form, name } = formAndName;
    this.removeChildForm(name);
    this.group.removeControl(name);
    form.disable();
  }

  protected buildSchema(details: ContactDetails, { root, native }: FormStateSchema): FormRootSchema {
    return root({
      mode: this.mode,
      enabled: whenCreateOrEditMode(),
      optimisticLock: true,
      children: {
        id: void 0,
        version: void 0,
        common: native(this.commonForm.group),
        ...mapValues(keyBy(this.getTabForms(details), 'name'), (form) => native(form.form.group)),
      },
    });
  }

  private getTabForms(details: ContactDetails): FormAndName[] {
    return details.common.contactTypes
      .filter((type) => type.visible && type.assigned)
      .reduce((acc, type) => acc.concat(this.findTabForm(type.type)), <FormAndName[]>[]);
  }
}
